import { h, Component } from 'preact';
import { joinClasses, accessNested } from 'utils/utils';
import { getValueForClientSize } from 'utils/bolt';
import tryToPlayVideo from 'utils/tryToPlayVideo';
import Store from 'store/store';
import { onMarkdownClick } from 'utils/bolt';
import HTML from 'components/core/html/html.js';
import s from 'components/bolt/socialWall/socialWall.sss';

export default class SocialWall extends Component {
	constructor(props) {
		super(props);
		this.handleLoad = this.handleLoad.bind(this);
		this.handleError = this.handleError.bind(this);
		this.state = {
			isLoading: true,
			error: false
		};
	}

	componentDidMount() {
		const currentPage = accessNested(this.props, ['appData', 'pages', this.props.id]) || {};
		const data = currentPage.data || {};
		const { curatorId } = data;
		const config = { attributes: true, childList: true, subtree: true };
		const callback = function (mutationsList) {
			for (let mutation of mutationsList) {
				if (mutation.type === 'childList') {
					if (mutation.removedNodes.length > 0 && mutation.removedNodes[0].className === 'crt-popup-container') {
						Store.emit('EMBEDDED_VIDEO_PAUSE');
					}
				}
			}
		};
		this.initialiseCurator(curatorId);
		this.observer = new MutationObserver(callback);
		this.observer.observe(document.body, config);
		document.body.addEventListener('play', this.onCuratorVidPlay, true);
		document.body.addEventListener('pause', this.onCuratorVidPause, true);
	}

	componentWillUnmount() {
		this.observer.disconnect();
		document.body.removeEventListener('play', this.onCuratorVidPlay, true);
		document.body.removeEventListener('pause', this.onCuratorVidPause, true);
	}

	onCuratorVidPlay(e) {
		if (e.target?.parentElement?.className === 'popup-video-container') {
			Store.emit('EMBEDDED_VIDEO_PLAY');
		}
	}

	onCuratorVidPause(e) {
		if (e.target?.parentElement?.className === 'popup-video-container') {
			Store.emit('EMBEDDED_VIDEO_PAUSE');
		}
	}

	onCanPlayVideo(e) {
		tryToPlayVideo(e.target).catch(this.removeBgVideo);
	}

	removeBgVideo() {
		this.setState({ removeBgVideo: true });
	}

	handleLoad() {
		this.setState({ isLoading: false });
	}

	handleError() {
		this.setState({ error: true });
	}

	initialiseCurator(id) {
		const src = `https://cdn.curator.io/published/${id}.js`;
		if (document.querySelector(`script[src="${src}"]`)) {
			return this.handleLoad();
		}
		const script = document.createElement('script');
		script.async = true;
		script.src = src;
		script.onload = this.handleLoad;
		script.onerror = this.handleError;
		document.body.append(script);
	}

	getWall(id, feed = 'default') {
		const baseCopy = accessNested(this.props, 'appData.copy.baseCopy', {});
		const { isLoading, error } = this.state;
		const showLoader = isLoading && !error;
		return (
			<div class={joinClasses(s.wall, showLoader && s.loading)}>
				<CuratorContainer id={`curator-feed-${feed}-layout`} curatorId={id} key={`${feed}-${id}`} />
				<div class={s.loader} />
				{error && (
					<p class={joinClasses(s.error, 'medium')}>
						{baseCopy.$SOCIAL_WALL_LOAD_ERROR}
						<button class={s.btn} onClick={() => window.location.reload()}>
							{baseCopy.$SOCIAL_WALL_REFRESH}
						</button>
					</p>
				)}
			</div>
		);
	}

	renderHeading(heading, fontStyles) {
		return (
			<div class={s.heading}>
				<HTML
					class={fontStyles.socialWallHeader || fontStyles.synopsisTagline}
					markdown
					content={heading}
				/>
			</div>
		);
	}

	renderDescription(description, fontStyles) {
		return (
			<div class={s.description}>
				<HTML
					class={fontStyles.socialWallDescription || fontStyles.synopsisBody}
					markdown
					content={description}
					onClickCapture={this.onClick}
				/>
			</div>
		);
	}

	onClick(e) {
		onMarkdownClick(e);
	}

	render(props) {
		const currentPage = accessNested(props, ['appData', 'pages', props.id]) || {};
		const data = currentPage.data || {};
		const { curatorId, feed, heading, description } = data;
		const fontStyles = accessNested(this.props, 'appData.styling.fontVariables') || {};

		const bg = getValueForClientSize(data.bg);
		let isVideo;
		const containerStyle = {};
		if (bg?.asset) {
			isVideo = bg.asset.trim().endsWith('mp4');
			containerStyle.backgroundImage = isVideo ? `url(${bg.fallbackImg})` : `url(${bg.asset})`;
			containerStyle.backgroundPosition = bg.bgPosition || 'top';
		}

		return (
			<div class={s.container} data-section={props.id} style={containerStyle}>
				{isVideo && (
					<video
						class={joinClasses(s.bgVideo, this.state.removeBgVideo && s.hide)}
						src={bg.asset}
						autoPlay
						muted
						playsInline
						onCanPlay={this.onCanPlayVideo}
						style={{ objectPosition: bg.bgPosition || 'top' }}
						loop={!bg.disableLoop}
					/>
				)}
				<img class={s.tt} src={getValueForClientSize(data.tt, props.client)} alt={data.tt?.alt} />
				{this.renderHeading(heading, fontStyles)}
				{this.renderDescription(description, fontStyles)}
				{this.getWall(curatorId, feed)}
			</div>
		);
	}
}

// Cache containers as the curator loader will not re-run / repopulate things
const curatorContainers = {};
class CuratorContainer extends Component {
	shouldComponentUpdate() {
		return false;
	}

	componentDidMount() {
		const { id, curatorId } = this.props;
		// Should not happen but in case the id from props gets updated midway through the lifecycle
		// We should re-use the same initial id in willUnmount, so store it
		this.id = `${id}-${curatorId}`;
		if (!curatorContainers[this.id]) {
			let existing = document.querySelectorAll(`#${id}`);
			existing.forEach(e => e.id += '-old');
			const el = document.createElement('div');
			el.id = id;
			curatorContainers[this.id] = el;
		}
		this.base.append(curatorContainers[this.id]);
	}

	componentWillUnmount() {
		// Avoids the content getting cleaned up by preact
		curatorContainers[this.id]?.remove();
	}

	render({ id, curatorId, ...props }) {
		return <div {...props} />;
	}
}